import * as React from 'react';
import {Switch, Route, Redirect} from 'react-router-dom';

// @ts-expect-error - imported from .js/.jsx file
import {AdminLockIcon} from 'shells/icon/svg/admin-lock-icon';
import {INTEGRATIONS_DATA} from 'shells/integrations/integrations-page-content/integrations-data';
import type {Breadcrumb} from 'shells/layout/page-title-breadcrumbs';

import {setBodyId} from '../../../dom/set-body-id';

import {SetupPage} from '../setup-page';

import {IntegrationsListPage} from './integrations-list-page';
import {IntegrationsPageWrapper} from './integrations-page-wrapper';
import {CustomQuote} from './pages/custom-quote/custom-quote';
import {IndividualIntegrationPageWrapper} from './pages/individual-integration-page/individual-integration-page-wrapper';
import {Zapier} from './pages/zapier/zapier';

export function IntegrationsNewRoot() {
    return (
        <Switch>
            <Redirect exact={true} from='/setup/integration' to='/setup/integration/instant' />
            <Route
                exact={true}
                path='/setup/integration/instant'
                render={(routeProps) => {
                    const titles = getIntegrationsPageTitles(routeProps.location.pathname);
                    // Used for setup-integration.js to give functionality to buttons
                    setBodyId('list-integration');

                    return (
                        <IntegrationsPageWrapper
                            {...routeProps}
                            breadcrumbsTitle={titles.breadcrumbsTitle}
                            breadcrumbs={titles.breadcrumbs}
                            glossaryKey={titles.glossaryKey}
                            content={titles.content}
                        />
                    );
                }}
            />
            <Route
                exact={true}
                path='/setup/integration/zapier'
                render={(routeProps) => {
                    const titles = getIntegrationsPageTitles(routeProps.location.pathname);
                    // Used for setup-integration.js to give functionality to buttons
                    setBodyId('list-integration');

                    return (
                        <IntegrationsPageWrapper
                            {...routeProps}
                            breadcrumbsTitle={titles.breadcrumbsTitle}
                            breadcrumbs={titles.breadcrumbs}
                            glossaryKey={titles.glossaryKey}
                            content={titles.content}
                        />
                    );
                }}
            />
            <Route
                exact={true}
                path='/setup/integration/custom-quote'
                render={(routeProps) => {
                    const titles = getIntegrationsPageTitles(routeProps.location.pathname);

                    return (
                        <IntegrationsPageWrapper
                            {...routeProps}
                            breadcrumbsTitle={titles.breadcrumbsTitle}
                            breadcrumbs={titles.breadcrumbs}
                            glossaryKey={titles.glossaryKey}
                            content={titles.content}
                        />
                    );
                }}
            />
            <Route
                exact={true}
                path='/setup/integration/:category'
                render={(routeProps) => {
                    const {category} = routeProps.match.params;
                    const titles = getIntegrationsPageTitles(routeProps.location.pathname);

                    return (
                        <IntegrationsPageWrapper
                            {...routeProps}
                            breadcrumbsTitle={getCategoryFromCategoryId(category || '')}
                            breadcrumbs={titles.breadcrumbs}
                            glossaryKey={titles.glossaryKey}
                            content={<IntegrationsListPage {...routeProps} />}
                        />
                    );
                }}
            />
            <Route
                exact={true}
                path='/setup/integration/:category/:integrationId'
                render={(routeProps) => {
                    const {integrationId} = routeProps.match.params;
                    const titles = getIntegrationsPageTitles(
                        routeProps.location.pathname,
                        integrationId || ''
                    );

                    return (
                        <IntegrationsPageWrapper
                            {...routeProps}
                            breadcrumbsTitle={getNameByIntegrationId(integrationId || '')}
                            breadcrumbs={titles.breadcrumbs}
                            glossaryKey={titles.glossaryKey}
                            content={
                                <IndividualIntegrationPageWrapper
                                    integrationId={integrationId || ''}
                                />
                            }
                        />
                    );
                }}
            />
        </Switch>
    );
}

export function getIntegrationsPageTitles(
    pathname: string,
    integrationId?: string
): {
    pageTitle: string;
    pageSubTitle: string | React.ReactNode;
    pageIcon: React.ComponentType<any>;
    glossaryKey?: string;
    breadcrumbsTitle?: string;
    breadcrumbs?: Breadcrumb[];
    content?: React.ReactNode;
} {
    const glossaryKey = '/setup/integration';
    const breadcrumbs = getBreadcrumbs(pathname, integrationId);
    if (pathname.includes('/setup/integration/instant')) {
        return {
            pageTitle: 'Instant integrations',
            pageSubTitle:
                'Learn about instant integrations that can help you get more out of Nutshell.',
            pageIcon: AdminLockIcon,
            glossaryKey,
            breadcrumbsTitle: 'Instant',
            breadcrumbs,
            content: (
                <SetupPage
                    pageTitle='Integrations'
                    viewUrl='/setup/instant-integration'
                    viewParams=''
                />
            ),
        };
    }

    if (pathname.includes('/setup/integration/zapier')) {
        return {
            pageTitle: 'Zapier integrations',
            pageSubTitle: 'Learn about Zapier integrations.',
            pageIcon: AdminLockIcon,
            glossaryKey,
            breadcrumbsTitle: 'Zapier',
            breadcrumbs,
            content: <Zapier />,
        };
    }

    if (pathname.includes('/setup/integration/custom-quote')) {
        return {
            pageTitle: 'Custom quote',
            pageSubTitle: 'Get a quote for a custom integration.',
            pageIcon: AdminLockIcon,
            glossaryKey,
            breadcrumbsTitle: 'Custom Quote',
            breadcrumbs,
            content: <CustomQuote />,
        };
    }

    if (pathname.includes('/setup/integration')) {
        return {
            pageTitle: 'Integrations',
            pageSubTitle: 'Learn about integrations that can help you get more out of Nutshell.',
            pageIcon: AdminLockIcon,
            breadcrumbs,
            glossaryKey,
        };
    }

    // @ts-expect-error
    return {};
}

function getBreadcrumbs(pathname: string, integrationId?: string): any[] {
    const breadcrumbs = [{to: '/setup/integration/', text: 'Integrations'}];
    if (integrationId) {
        if (pathname.includes('/setup/integration/all/')) {
            breadcrumbs.push({to: '/setup/integration/all', text: 'All'});
        }
        if (pathname.includes('/setup/integration/accounting/')) {
            breadcrumbs.push({to: '/setup/integration/accounting', text: 'Accounting'});
        }
        if (pathname.includes('/setup/integration/collaboration/')) {
            breadcrumbs.push({to: '/setup/integration/collaboration', text: 'Collaboration'});
        }
        if (pathname.includes('/setup/integration/communication/')) {
            breadcrumbs.push({
                to: '/setup/integration/communication',
                text: 'Communication',
            });
        }
        if (pathname.includes('/setup/integration/contracts/')) {
            breadcrumbs.push({
                to: '/setup/integration/contracts',
                text: 'Contracts',
            });
        }
        if (pathname.includes('/setup/integration/forms/')) {
            breadcrumbs.push({to: '/setup/integration/forms', text: 'Forms'});
        }
        if (pathname.includes('/setup/integration/lead_generation/')) {
            breadcrumbs.push({
                to: '/setup/integration/lead_generation',
                text: 'Lead generation',
            });
        }
        if (pathname.includes('/setup/integration/scheduling/')) {
            breadcrumbs.push({to: '/setup/integration/scheduling', text: 'Scheduling'});
        }
        if (pathname.includes('/setup/integration/webinars/')) {
            breadcrumbs.push({to: '/setup/integration/webinars', text: 'Webinars'});
        }
        if (pathname.includes('/setup/integration/other/')) {
            breadcrumbs.push({to: '/setup/integration/other', text: 'Other'});
        }
    }

    return breadcrumbs;
}

// Return the breadcrumb text for a category based on the category id
function getCategoryFromCategoryId(categoryId: string): string {
    if (categoryId === 'all') {
        return 'All';
    } else if (categoryId === 'accounting') {
        return 'Accounting';
    } else if (categoryId === 'collaboration') {
        return 'Collaboration';
    } else if (categoryId === 'communication') {
        return 'Communication & phone';
    } else if (categoryId === 'contracts') {
        return 'Contracts & proposals';
    } else if (categoryId === 'forms') {
        return 'Forms';
    } else if (categoryId === 'lead_generation') {
        return 'Lead generation';
    } else if (categoryId === 'scheduling') {
        return 'Scheduling';
    } else if (categoryId === 'webinars') {
        return 'Webinars & events';
    } else if (categoryId === 'other') {
        return 'Other';
    }

    return '';
}

function getNameByIntegrationId(integrationId: string): string {
    // find the proper category for the integration id
    const matchingItem = INTEGRATIONS_DATA.find(
        (integration) => integration.integrationId === integrationId
    );
    let matchingName = '';
    if (matchingItem && matchingItem.name) {
        matchingName = matchingItem.name;
    }

    return matchingName;
}
